import BftBooleanButton from "common/components/boolean-button";
import CountriesDropdown from "common/components/countries-dropdown";
import BftDropzone from "common/components/dropzone";
import BftInfoTooltip from "common/components/info-tooltip";
import BftInput from "common/components/input";
import BftSelect from "common/components/select";
import { TipoSedeEnum } from "common/enums/sede.enum";
import { COUNTRIES } from "constants/countries";
import { BooleanValues, IdTipoDocumenti, IdTipoDocumentiEstesi } from "constants/global";
import { commonGetComuni } from "core/store/actions";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { commonComuniSelector, commonProvinceSelector } from "core/store/selectors";
import { FieldArrayRenderProps, FormikErrors, FormikTouched } from "formik";
import { FileDocument } from "models/files";
import { ChangeEvent, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { DropdownProps } from "semantic-ui-react";
import { FormValues, SedeDtoExt } from "..";

const { TRUE, FALSE } = BooleanValues;

interface Props {
	arrayHelpers: FieldArrayRenderProps;
	values: FormValues;
	errors: FormikErrors<FormValues>;
	touched: FormikTouched<FormValues>;
	handleBlur: (e: React.FocusEvent<any, Element>) => void;
	handleChange: {
		(e: React.ChangeEvent<any>): void;
		<T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any>
			? void
			: (e: string | React.ChangeEvent<any>) => void;
	};
	setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
}

const IndirizziContattiAltraSede: React.FC<Props> = (props) => {
	const {
		values,
		arrayHelpers,
		errors: _errors,
		touched: _touched,
		handleBlur,
		handleChange: _handleChange,
		setFieldValue,
	} = props;

	const dispatch = useAppDispatch();

	const province = useAppSelector(commonProvinceSelector);
	const comuni = useAppSelector(commonComuniSelector);

	const [sedeIndex, setSedeIndex] = useState<number>();

	const sede = sedeIndex != undefined ? values?.elencoSedi?.[sedeIndex] : undefined;
	const namePrefix = `elencoSedi[${sedeIndex}]`;
	const errors =
		sedeIndex != undefined
			? (_errors?.elencoSedi?.[sedeIndex] as FormikErrors<SedeDtoExt>)
			: undefined;
	const touched =
		sedeIndex != undefined
			? (_touched?.elencoSedi?.[sedeIndex] as FormikTouched<SedeDtoExt>)
			: undefined;

	const handleSedeChecked = (event: ChangeEvent<HTMLInputElement>) => {
		if (event?.currentTarget?.checked && !sede) {
			arrayHelpers.push({
				indirizzo: "",
				numeroCivico: "",
				cap: "",
				comune: "",
				provincia: "",
				nazione: "it",
				operativa: true,
				nomeSoggettoPreposto: "",
				cognomeSoggettoPreposto: "",
				emailSoggettoPreposto: "",
				telefonoSoggettoPreposto: "",
				tipoSede: TipoSedeEnum.ALTRA_SEDE_OPERATIVA,
				prepostoIsRL: TRUE,
				SCO_CONTRATTO_UTENZA_LOCAZIONEDCM: undefined,
			} as SedeDtoExt);
		} else {
			handleChange(event);
		}
	};

	const handlePrepostoCheck = (value: string | undefined) => {
		if (value) _handleChange(`${namePrefix}.prepostoIsRL`)(value);
	};

	const handleCountryChange = (event: DropdownProps) => {
		const selectedValue = event?.value;
		if (selectedValue) _handleChange(`${namePrefix}.nazione`)(String(selectedValue)?.toUpperCase());
	};

	const handleContrattoLocazioneChange = (file: FileDocument | null) => {
		setFieldValue(`${namePrefix}.SCO_CONTRATTO_UTENZA_LOCAZIONEDCM`, file);
	};

	const handleChange = (event: ChangeEvent<any>) => {
		if (event?.currentTarget?.name?.includes("provincia")) {
			dispatch(commonGetComuni(event?.target?.value));
		}

		_handleChange(event);
	};

	useEffect(() => {
		let index = values?.elencoSedi?.findIndex(
			(s) => s?.tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA
		);

		setSedeIndex(index);
	}, [values]);

	return (
		<div>
			<Form.Check
				type="checkbox"
				className={`bft-radio ${sede?.operativa ? "checked opened-preposto" : ""}`}
				label="Indica qui altre Sedi Operative ove presenti"
				name={`${namePrefix}.operativa`}
				checked={sede?.operativa}
				onChange={handleSedeChecked}
				onBlur={handleBlur}
			/>

			<input hidden name={`${namePrefix}.tipoSede`} value={sede?.tipoSede} />

			{sede?.operativa && (
				<div className="container-form-preposto pt-3">
					<Row className="">
						<Col md={5}>
							<BftSelect
								name={`${namePrefix}.provincia`}
								placeholder={"Provincia*"}
								value={sede?.provincia}
								onChange={handleChange}
								options={province}
								isInvalid={!!errors?.provincia && touched?.provincia}
							/>
							{errors?.provincia && touched?.provincia && (
								<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
									{errors?.provincia}
								</Form.Control.Feedback>
							)}
						</Col>

						<Col md={7}>
							<BftSelect
								name={`${namePrefix}.comune`}
								placeholder={"Comune*"}
								value={sede?.comune}
								onChange={handleChange}
								options={comuni?.[sede?.provincia!]}
								isInvalid={!!(touched?.comune && errors?.comune)}
							/>
							{errors?.comune && touched?.comune && (
								<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
									{errors?.comune}
								</Form.Control.Feedback>
							)}
						</Col>

						<Col md={5}>
							<BftInput
								required
								type={"text"}
								name={`${namePrefix}.cap`}
								placeholder={"CAP*"}
								value={sede?.cap}
								onChange={handleChange}
								isInvalid={!!(touched?.cap && errors?.cap)}
							/>
							{errors?.cap && touched?.cap && (
								<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
									{errors?.cap}
								</Form.Control.Feedback>
							)}
						</Col>

						<Col md={7} className="mb-3">
							<CountriesDropdown
								options={COUNTRIES}
								value={sede?.nazione}
								onChange={(e, d) => {
									handleCountryChange(d);
								}}
							/>
						</Col>

						<Col md={9}>
							<BftInput
								required
								type={"text"}
								name={`${namePrefix}.indirizzo`}
								placeholder={"Indirizzo*"}
								value={sede?.indirizzo}
								onChange={(e) => {
									const { name, value } = e.target;
									setFieldValue(name, value.toUpperCase());
								}}
								isInvalid={!!(touched?.indirizzo && errors?.indirizzo)}
							/>
							{errors?.indirizzo && touched?.indirizzo && (
								<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
									{errors?.indirizzo}
								</Form.Control.Feedback>
							)}
						</Col>

						<Col md={3}>
							<BftInput
								required
								type={"text"}
								name={`${namePrefix}.numeroCivico`}
								placeholder={"N°*"}
								value={sede?.numeroCivico}
								onChange={handleChange}
								isInvalid={!!(touched?.numeroCivico && errors?.numeroCivico)}
							/>
							{errors?.numeroCivico && touched?.numeroCivico && (
								<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
									{errors?.numeroCivico}
								</Form.Control.Feedback>
							)}
						</Col>
					</Row>

					<div className="d-flex mb-2 justify-content-between align-items-center">
						<div className="d-flex">
							<span className="mb-0 ms-3 mx-0 text-secondary">
								L'operatore della sede coincide con il legale rappresentante?
							</span>

							<span>
								<BftInfoTooltip
									message="Indicare i dati del soggetto preposto alla sede secondaria"
									className="mt-1"
								/>
							</span>
						</div>

						<div>
							<BftBooleanButton
								label=""
								name={`${namePrefix}.prepostoIsRL`}
								value={sede?.prepostoIsRL}
								onChange={handlePrepostoCheck}
							/>
						</div>
					</div>

					{sede?.prepostoIsRL == FALSE && (
						<Row>
							<Col md={6}>
								<BftInput
									required
									type={"text"}
									name={`${namePrefix}.nomeSoggettoPreposto`}
									placeholder={"Nome preposto*"}
									value={sede?.nomeSoggettoPreposto?.toUpperCase()}
									onChange={handleChange}
									isInvalid={!!(touched?.nomeSoggettoPreposto && errors?.nomeSoggettoPreposto)}
								/>
								{errors?.nomeSoggettoPreposto && touched?.nomeSoggettoPreposto && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors?.nomeSoggettoPreposto}
									</Form.Control.Feedback>
								)}
							</Col>

							<Col md={6}>
								<BftInput
									required
									type={"text"}
									name={`${namePrefix}.cognomeSoggettoPreposto`}
									placeholder={"Cognome preposto*"}
									value={sede?.cognomeSoggettoPreposto?.toUpperCase()}
									onChange={handleChange}
									isInvalid={
										!!(touched?.cognomeSoggettoPreposto && errors?.cognomeSoggettoPreposto)
									}
								/>
								{errors?.cognomeSoggettoPreposto && touched?.cognomeSoggettoPreposto && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors?.cognomeSoggettoPreposto}
									</Form.Control.Feedback>
								)}
							</Col>

							<Col md={6}>
								<BftInput
									required
									type={"text"}
									name={`${namePrefix}.emailSoggettoPreposto`}
									placeholder={"Email preposto*"}
									value={sede?.emailSoggettoPreposto}
									onChange={handleChange}
									isInvalid={!!(touched?.emailSoggettoPreposto && errors?.emailSoggettoPreposto)}
								/>
								{errors?.emailSoggettoPreposto && touched?.emailSoggettoPreposto && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors?.emailSoggettoPreposto}
									</Form.Control.Feedback>
								)}
							</Col>

							<Col md={6}>
								<BftInput
									required
									type={"text"}
									name={`${namePrefix}.telefonoSoggettoPreposto`}
									placeholder={"Cellulare preposto*"}
									value={sede?.telefonoSoggettoPreposto}
									onChange={handleChange}
									isInvalid={
										!!(touched?.telefonoSoggettoPreposto && errors?.telefonoSoggettoPreposto)
									}
								/>
								{errors?.telefonoSoggettoPreposto && touched?.telefonoSoggettoPreposto && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors?.telefonoSoggettoPreposto}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>
					)}

					<div className="mb-3 container d-flex justify-content-center">
						<h6>Utenza o contratto di locazione*</h6>
						<BftInfoTooltip
							className="ms-3"
							message="Allegare una copia di un contratto di fornitura telefonica, elettrica, di gas da cui emerga l'indirizzo della seconda sede piuttosto che una copia del contratto di locazione del locale."
						/>
					</div>

					<Row className="mb-3">
						<Col md={12}>
							<BftDropzone
								label=""
								idTipoDocumento={IdTipoDocumenti.CONTRATTO_UTENZA_LOCAZIONE}
								trascinaDocumento={IdTipoDocumentiEstesi?.CONTRATTO_UTENZA_LOCAZIONE}
								file={sede?.SCO_CONTRATTO_UTENZA_LOCAZIONEDCM}
								onChange={(e) => handleContrattoLocazioneChange(e)}
								isInvalid={
									!!errors?.SCO_CONTRATTO_UTENZA_LOCAZIONEDCM &&
									(touched?.SCO_CONTRATTO_UTENZA_LOCAZIONEDCM ||
										touched?.SCO_CONTRATTO_UTENZA_LOCAZIONEDCM == undefined)
								}
							/>
						</Col>
					</Row>
				</div>
			)}
		</div>
	);
};
export default IndirizziContattiAltraSede;
